import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { CreativeStatus } from 'src/app/data/constants/creative-status.const';
import { PaymentOverallStatus } from 'src/app/data/constants/payment-status.enum';
import { CreativeListVO } from 'src/app/data/vo/CreativeListVO';
import { CreativeFormats, CreativeVO } from 'src/app/data/vo/CreativeVO';
import { ModalService } from 'src/app/services/modal.service';
import { StorageService } from 'src/app/services/storage.service';
import { BaseAdapter } from 'src/whiteLabel/mediaCorps/adapters/base-adapter';
import { api } from 'src/whiteLabel/mediaCorps/services/creative/creative-api.service';
import {
    CampaignAssociations,
    CampaignCreativeData,
    CampaignCreativeStatus,
    CreativeData,
    CreativeFormats as ApiCreativeFormats,
    CreativePaymentStatus,
    CreativeStatus as ApiCreativeStatus,
    CreativeStatusData,
    ProcessingStatus,
    AdDurationData
} from 'src/whiteLabel/mediaCorps/services/creative/creative-data';
import { ConnectionData } from 'src/whiteLabel/mediaCorps/services/misc/misc-data';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class CreativeAdapter extends BaseAdapter {

    constructor(private http: HttpClient, private storage: StorageService, protected modal: ModalService) {
        super(http, storage, modal);
    }

    public getAllCreativeStatus(): Promise<any> {
        return this.getCBAId().then((cba) => {
            return api.getAllCreativeStatus(this.http, cba.brandId);
        }).then();
    }

    public getAllCreativeLists(): Promise<Array<CreativeListVO>> {
        let allPromise = [];
        // having reject handler so that it will always guarantee a list respone in Promise.all
        let statusResolvedPromise = this.getCBAId().then((cba) => {
            return api.getAllCreativeStatus(this.http, cba.brandId).catch((err: any) => {
                console.error('Error on fetch all creative status: ', err);
                return [];
            });
        }).then();
        let creativeResolvedPromise = this.getCBAId().then((cba) => {
            return api.getAllCreatives(this.http, cba.brandId).catch((err: any) => {
                console.error('Error on fetch all creative data: ', err);
                return [];
            });
        }).then();

        allPromise.push(creativeResolvedPromise);
        allPromise.push(statusResolvedPromise);
        return Promise.all(allPromise).then((response: any[]) => {
            let creatives: CreativeData[] = response[0];
            let status: CreativeStatusData[] = response[1];
            let creativeIdStatusMap = {};
            let creativeIdsToCheckPayment: number[] = [];
            status.forEach((value: CreativeStatusData) => {
                creativeIdStatusMap[value.creative_id] = value;
            });
            response[1] = creativeIdStatusMap;
            creatives.forEach((data: CreativeData) => {
                if (data.creative_author === 'Seller' && creativeIdStatusMap[data.creative_id] &&
                    creativeIdStatusMap[data.creative_id].creative_status === ApiCreativeStatus.NotAvailable &&
                    creativeIdStatusMap[data.creative_id].processing_status === ProcessingStatus.AwaitingMedia) {
                    creativeIdsToCheckPayment.push(data.creative_id);
                }
            });
            if (creativeIdsToCheckPayment.length > 0) {
                // TODO: We might want to do sublist if this list becomes too large?
                return api.getCreativesPaymentStatus(this.http, creativeIdsToCheckPayment).then((cpss: Array<CreativePaymentStatus>) => {
                    let unpaidCreativeIds = [];
                    cpss.forEach((cps) => {
                        if (cps.payment_status !== PaymentOverallStatus.FULLY_PAID) {
                            unpaidCreativeIds.push(cps.entity_id);
                        }
                    });
                    response[2] = unpaidCreativeIds;
                    return response;
                }).then();
            } else {
                return response;
            }
        }).then((resp: any[]) => {
            let creatives: CreativeData[] = resp[0];
            let creativeIdStatusMap = resp[1];
            let unpaidCreativeIds = resp[2];
            if (unpaidCreativeIds && unpaidCreativeIds.length > 0) {
                creatives = creatives.filter((c) => !unpaidCreativeIds.includes(c.creative_id));
            }
            let returnData: CreativeListVO[] = creatives.map((data: CreativeData) => {
                let listVO: CreativeListVO = {
                    creativeId: data.creative_id,
                    name: data.name,
                    adDuration: data.ad_duration,
                    language: data.language,
                    type: data.creative_author === 'Buyer' ? 'User created' : data.creative_author === 'Seller' ? 'Mediacorp' : data.creative_author,
                    campaigns: this.convertCreativeCampaignsWhiteLabelDataToVO(data.campaigns),
                    creativeFormats: data.creative_formats && data.creative_formats.map((cf) => {
                        return {
                            cdnUrl: cf.cdn_url,
                            formatType: cf.format_type
                        };
                    }),
                    // default to processing
                    status: creativeIdStatusMap[data.creative_id] ? this.convertCreativeStatus(creativeIdStatusMap[data.creative_id].creative_status, creativeIdStatusMap[data.creative_id].processing_status) : CreativeStatus.Processing.key,
                    creativeStatusNotes: creativeIdStatusMap[data.creative_id] && creativeIdStatusMap[data.creative_id].creative_status === ApiCreativeStatus.Rejected ? this.extractStatusMessage(creativeIdStatusMap[data.creative_id].creative_status_notes) : undefined,
                    externalIdentifier: data.external_identifier
                };
                return listVO;
            });
            return returnData;
        });
    }

    private extractStatusMessage(message: string): string {
        // sample message Created By: TvPlatformAPIUser@amobee.com (11/18/2020 9:37 AM SGT) Creative quality is poor.
        // confirmed with api team that this message format will be consistent, remove the user and time info
        if (message) {
            let strArr = message.trim().split(' ');
            strArr.splice(0, 7);
            return strArr.join(' ');
        }
        return undefined;
    }

    public getAllCreatives(creativeIds?: Array<number>): Promise<Array<CreativeVO>> {
        return this.getCBAId().then((cba) => {
            return api.getAllCreatives(this.http, cba.brandId, creativeIds).then(
                (response: CreativeData[]) => {
                    let returnedData = [];
                    if (response) {
                        returnedData = response.map(this.convertCreativeWhiteLabelDataToVO, this);
                    }
                    return returnedData;
                }
            );
        }).then();
    }

    public getAllAssignableCreatives(): Promise<Array<CreativeVO>> {
        let allPromise = [];
        let statusResolvedPromise = this.getCBAId().then((cba) => {
            return api.getAllCreativeStatus(this.http, cba.brandId).catch((err: any) => {
                console.error('Error on fetch all creative status: ', err);
                return [];
            });
        })
        let creativeResolvedPromise = this.getCBAId().then((cba) => {
            return api.getAllCreatives(this.http, cba.brandId).catch((err: any) => {
                console.error('Error on fetch all creative data: ', err);
                return [];
            });
        })

        allPromise.push(creativeResolvedPromise);
        allPromise.push(statusResolvedPromise);
        return Promise.all(allPromise).then((response: any[]) => {
            let creatives: CreativeData[] = response[0];
            let status: CreativeStatusData[] = response[1];
            let filteredCreativeIds = [];
            let creativeIdStatusMap = {};
            status.forEach((value: CreativeStatusData) => {
                if (value.creative_status !== ApiCreativeStatus.Rejected &&
                    (value.creative_status !== ApiCreativeStatus.NotAvailable || value.processing_status === ProcessingStatus.TranscodingComplete
                        || value.processing_status === ProcessingStatus.TranscodeComplete
                        || value.processing_status === ProcessingStatus.AwaitingMedia
                        || value.processing_status === ProcessingStatus.SubmittedForTranscoding)) {
                    filteredCreativeIds.push(value.creative_id);
                }
                creativeIdStatusMap[value.creative_id] = value;
            });
            if (filteredCreativeIds.length) {
                creatives = creatives.filter((creative) => {
                    creative.creative_status = creativeIdStatusMap[creative.creative_id].creative_status;
                    creative.processing_status = creativeIdStatusMap[creative.creative_id].processing_status;
                    return filteredCreativeIds.includes(creative.creative_id);
                });
                response[0] = creatives;
            } else {
                response[0] = [];
            }
            return response;
        }).then((resp: any[]) => {
            let creatives: CreativeData[] = resp[0];
            return creatives.map(this.convertCreativeWhiteLabelDataToVO, this);
        });
    }

    public createCreative(creatives: Array<CreativeVO>): Promise<Array<CreativeVO>> {
        let uuid;
        if (creatives && creatives.length > 1 && creatives[0].creativeAuthor === 'Seller') {
            uuid = uuidv4();
        }
        let requestData = creatives.map((cvo: CreativeVO) => {
            let data = this.convertCreativeVOToWhiteLabelData(cvo);
            if (uuid) {
                data.group_name = uuid;
            }
            return data;
        });
        return this.getCBAId().then((cba) => {
            return api.createCreative(this.http, cba.brandId, requestData).then((response: Array<CreativeData>) => {
                let returnedData = [];
                if (response) {
                    returnedData = response.map(this.convertCreativeWhiteLabelDataToVO, this);
                }
                return returnedData;
            });
        }).then();
    }

    public getCreativeById(creativeId: number): Promise<CreativeVO> {
        return this.getCBAId().then((cba) => {
            return api.getCreativeById(this.http, cba.brandId, creativeId).then((response: CreativeData) => {
                return this.convertCreativeWhiteLabelDataToVO(response);
            });
        }).then();
    }

    public updateCreative(creative: CreativeVO): Promise<any> {
        let requestData: CreativeData = this.convertCreativeVOToWhiteLabelData(creative);
        return this.getCBAId().then((cba) => {
            return api.updateCreative(this.http, cba.brandId, requestData).then((response: any) => {
                return response;
            });
        }).then();

    }

    public assignCreativesToCampaign(campaignId: number, creativeIds: number[]): Promise<any> {
        let data: Array<CampaignAssociations> = creativeIds.map((value: number) => {
            return {
                creative_id: value
            };
        });
        return api.assignCreativesToCampaign(this.http, campaignId, data).then((response: any) => {
            return response;
        });
    }

    public getCampaignCreativeStatus(campaignId: number): Promise<Array<{ creativeId: number, status: string }>> {
        return api.getCampaignCreativeStatus(this.http, campaignId).then((response: CampaignCreativeStatus) => {
            if (response.Result && response.Result.length > 0 && response.Result[0].FlightMediaManage && response.Result[0].FlightMediaManage.length > 0) {
                return response.Result[0].FlightMediaManage.map((v) => {
                    return {
                        creativeId: v.MediaId,
                        status: v.ApprovalStatus
                    };
                });
            }
            return null;
        });
    };

    public getCreativeByCampaignId(campaignId: number): Promise<Array<CreativeVO>> {
        return api.getCampaignCreatives(this.http, campaignId).then((response: Array<CampaignCreativeData>) => {
            if (response && response.length > 0) {
                let creativeIds: number[] = _.uniqBy(response, 'creative_id').map((c) => {
                    return c.creative_id;
                });
                return this.getAllCreatives(creativeIds);
            }
            return [];
        });
    };

    public getCreativesPaymentStatus(creativeIds: number[]): Promise<Array<{ creativeId: number, status: PaymentOverallStatus }>> {
        return api.getCreativesPaymentStatus(this.http, creativeIds).then((response: CreativePaymentStatus[]) => {
            return response.map((cps: CreativePaymentStatus) => {
                return {
                    creativeId: cps.entity_id,
                    status: cps.payment_status
                };
            })
        });
    }

    // these methods should be fine to exist in white label but not in base folder as long as it is only using within the service class
    private convertCreativeWhiteLabelDataToVO(data: CreativeData): CreativeVO {
        let vo: CreativeVO = {
            creativeId: data.creative_id,
            name: data.name,
            adDuration: data.ad_duration,
            language: data.language,
            creativeAuthor: data.creative_author,
            creativeFormats: data.creative_formats.map((cf) => {
                let result: CreativeFormats = {
                    cdnUrl: cf.cdn_url,
                    formatType: cf.format_type
                };
                return result;
            }),
            creative_product_id: data.creative_product_id,
            creative_status: this.convertCreativeStatus(data.creative_status, '')
        };
        this.filterProcessingCreative(data, vo);
        return vo;
    };

    private convertCreativeVOToWhiteLabelData(vo: CreativeVO): CreativeData {
        let data = new CreativeData();
        data.creative_id = vo.creativeId;
        data.name = vo.name;
        data.ad_duration = vo.adDuration;
        data.language = vo.language;
        data.creative_formats = vo.creativeFormats.map((v) => {
            let cf = new ApiCreativeFormats();
            cf.cdn_url = v.cdnUrl;
            cf.format_type = v.formatType;
            return cf;
        });
        data.creative_author = vo.creativeAuthor;
        data.creative_product_id = vo.creative_product_id;
        return data;
    }

    private convertCreativeStatus(creativeStatus: string, processingStatus: string): string {
        // default to processing
        let appCreativeStatus: string = CreativeStatus.Processing.key;;
        if (creativeStatus === ApiCreativeStatus.Approved) {
            appCreativeStatus = CreativeStatus.Approved.key;
        } else if (creativeStatus === ApiCreativeStatus.Rejected) {
            appCreativeStatus = CreativeStatus.Rejected.key;
        } else if ((creativeStatus === ApiCreativeStatus.Pending || creativeStatus === ApiCreativeStatus.NotAvailable) && processingStatus === ProcessingStatus.TranscodeComplete) {
            appCreativeStatus = CreativeStatus.Ready.key;
        } else if (creativeStatus === ApiCreativeStatus.Pending) {
            appCreativeStatus = CreativeStatus.Pending.key;
        } else if (processingStatus === ProcessingStatus.Processing) {
            appCreativeStatus = CreativeStatus.Processing.key;
        } else if (processingStatus === ProcessingStatus.Ready) {
            appCreativeStatus = CreativeStatus.Ready.key;
        }
        return appCreativeStatus;
    }

    private filterProcessingCreative(data, vo) {
        if (data.creative_product_id === 0 && data.processing_status !== ProcessingStatus.TranscodeComplete) {
            vo.name = data.name + ' (Processing)';
            vo.isDisabled = true;
        }
    }

    private convertCreativeCampaignsWhiteLabelDataToVO(campaigns: CreativeData['campaigns']): CreativeListVO['campaigns'] {
        if (campaigns && campaigns.length > 0) {
            if (campaigns.length !== 1 || campaigns[0].campaign_id !== 0) {
                campaigns = _.uniqBy(campaigns, 'campaign_id');
                return campaigns.map((c) => {
                    return {
                        campaignId: c.campaign_id,
                        campaignName: c.campaign_name
                    };
                });
            }
        }
        return [];
    }

    public getAdDurations(): Promise<Array<AdDurationData>> {
        return this.getConnections().then((connection: ConnectionData) => {
            return api.getAdsDurations(this.http, connection[0].connection_id).then((adsDurations: any) => {
                return adsDurations;
            });
        }).then();
    }
}
